import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Warning from "../../../../../Components/common/Warning/Warning";

import {
  requestTimePeriod,
  updateTimePeriodRequest,
  requestArtifactPeriod,
  updateArtifactPeriodRequest,
} from "../../../../../store/actions/settingsActions";

import "./styles.scss";

// Value the Reset button restores the retention period to.
const DEFAULT_TIME_PERIOD = 90;
// Artifact files in MinIO: default and allowed range, enforced by the backend too.
const DEFAULT_ARTIFACT_PERIOD = 3;
const MAX_ARTIFACT_PERIOD = 365;

const DataRetention = () => {
  const timePeriodFromDB = useSelector((state) => state.settings.timePeriod);
  const dispatch = useDispatch();
  const [timePeriod, setTimePeriod] = useState(timePeriodFromDB);
  const [showResetWarning, setShowResetWarning] = useState(false);
  const artifactPeriodFromDB = useSelector((state) => state.settings.artifactPeriod);
  const [artifactPeriod, setArtifactPeriod] = useState(artifactPeriodFromDB);
  const [showArtifactResetWarning, setShowArtifactResetWarning] = useState(false);

  useEffect(() => {
    dispatch(requestTimePeriod());
    dispatch(requestArtifactPeriod());
  }, [dispatch]);

  useEffect(() => {
    setTimePeriod(timePeriodFromDB);
  }, [timePeriodFromDB]);

  useEffect(() => {
    setArtifactPeriod(artifactPeriodFromDB);
  }, [artifactPeriodFromDB]);

  const timePeriodHandler = (e) => {
    const re = /^[0-9\b]+$/;

    if (e.target.value === "" || re.test(e.target.value)) {
      setTimePeriod(Number(e.target.value));
    }
  };

  const updateTimePeriodHandler = () => {
    if (timePeriod === "") return;
    dispatch(updateTimePeriodRequest({ time_period: timePeriod }));
  };

  const resetTimePeriodHandler = () => {
    setShowResetWarning(false);
    setTimePeriod(DEFAULT_TIME_PERIOD);
    dispatch(updateTimePeriodRequest({ time_period: DEFAULT_TIME_PERIOD }));
  };

  const artifactPeriodHandler = (e) => {
    const re = /^[0-9\b]+$/;

    if (e.target.value === "" || re.test(e.target.value)) {
      setArtifactPeriod(Number(e.target.value));
    }
  };

  const isArtifactPeriodValid = artifactPeriod >= 1 && artifactPeriod <= MAX_ARTIFACT_PERIOD;

  const updateArtifactPeriodHandler = () => {
    if (!isArtifactPeriodValid) return;
    dispatch(updateArtifactPeriodRequest({ artifact_period: artifactPeriod }));
  };

  const resetArtifactPeriodHandler = () => {
    setShowArtifactResetWarning(false);
    setArtifactPeriod(DEFAULT_ARTIFACT_PERIOD);
    dispatch(updateArtifactPeriodRequest({ artifact_period: DEFAULT_ARTIFACT_PERIOD }));
  };

  return (
    <>
      <div className="data-retention">
        <h1 className="data-retention__header">Data Retention</h1>
        <div className="data-retention__body">
          <div className="data-retention__body__header">
            <h3>Data retention</h3>
            <p>NOTE: Delete operation will be performed at 11pm everyday.</p>
          </div>
          <div className="data-retention__body__content">
            <div className="data-retention__field">
              <p className="data-retention__field__label">Time period</p>
              <input
                className="data-retention__field__input"
                value={timePeriod}
                onChange={timePeriodHandler}
                placeholder="Time period"
              />
              <span className="data-retention__field__unit">days</span>
            </div>
            <div className="data-retention__actions">
              <button
                className="m-r-15 default-button"
                onClick={() => setShowResetWarning(true)}
                disabled={timePeriodFromDB === DEFAULT_TIME_PERIOD}>
                Reset
              </button>
              <button
                className="primary-button"
                onClick={updateTimePeriodHandler}
                disabled={timePeriod === "" || timePeriod === timePeriodFromDB}>
                Update
              </button>
            </div>
          </div>
        </div>
        <Warning
          onClickOk={resetTimePeriodHandler}
          show={showResetWarning}
          setShow={setShowResetWarning}
          text={`The data retention period will be reset to ${DEFAULT_TIME_PERIOD} days. Do you want to continue?`}
          article="Reset"
        />
      </div>

      <div className="data-retention">
        <h1 className="data-retention__header">Artifact Retention</h1>
        <div className="data-retention__body">
          <div className="data-retention__body__header">
            <h3>Artifact retention</h3>
            <p>
              NOTE: Artifact files in storage older than this period are deleted. The check runs
              every hour.
            </p>
          </div>
          <div className="data-retention__body__content">
            <div className="data-retention__field">
              <p className="data-retention__field__label">Artifact files</p>
              <input
                className="data-retention__field__input"
                value={artifactPeriod}
                onChange={artifactPeriodHandler}
                placeholder="Time period"
              />
              <span className="data-retention__field__unit">days</span>
            </div>
            <p className="data-retention__field__hint">Between 1 and {MAX_ARTIFACT_PERIOD} days.</p>
            <div className="data-retention__actions">
              <button
                className="m-r-15 default-button"
                onClick={() => setShowArtifactResetWarning(true)}
                disabled={artifactPeriodFromDB === DEFAULT_ARTIFACT_PERIOD}>
                Reset
              </button>
              <button
                className="primary-button"
                onClick={updateArtifactPeriodHandler}
                disabled={!isArtifactPeriodValid || artifactPeriod === artifactPeriodFromDB}>
                Update
              </button>
            </div>
          </div>
        </div>
        <Warning
          onClickOk={resetArtifactPeriodHandler}
          show={showArtifactResetWarning}
          setShow={setShowArtifactResetWarning}
          text={`The artifact retention period will be reset to ${DEFAULT_ARTIFACT_PERIOD} days. Do you want to continue?`}
          article="Reset"
        />
      </div>
    </>
  );
};

export default DataRetention;
